import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  FormHelperText
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { collection, addDoc, getDocs, query, where, serverTimestamp } from 'firebase/firestore';
import { COLLECTIONS } from '../firebase/collections';
import { SERVICE_ROLES } from '../constants/services';
import { getServices } from '../services/serviceManagementService';

// Constantes pour les priorités
const PRIORITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  URGENT: 'urgent'
};

const PRIORITY_LABELS = {
  [PRIORITY.LOW]: 'Basse',
  [PRIORITY.MEDIUM]: 'Moyenne',
  [PRIORITY.HIGH]: 'Haute',
  [PRIORITY.URGENT]: 'Urgente'
};

// Catégories prédéfinies
const CATEGORIES = {
  PLUMBING: 'Plomberie',
  ELECTRICAL: 'Électricité',
  CARPENTRY: 'Menuiserie',
  PAINTING: 'Peinture',
  HVAC: 'Climatisation',
  IT: 'Informatique',
  CLEANING: 'Nettoyage',
  GARDENING: 'Jardinage',
  LOCKSMITH: 'Serrurerie',
  OTHER: 'Autre'
};

export default function WorkRequestForm() {
  const navigate = useNavigate();
  const { currentUser, userRole, userService, userSchool, userProfile } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: '',
    category: '',
    customCategory: '',
    location: '',
    assignedServiceId: '',
  });
  const [loading, setLoading] = useState(false);
  const [loadingServices, setLoadingServices] = useState(true);
  const [error, setError] = useState('');
  const [services, setServices] = useState([]);

  useEffect(() => {
    fetchServices();
  }, [userSchool]); // Recharger quand l'école change

  const fetchServices = async () => {
    if (!userSchool?.id) {
      console.log('WorkRequestFormPage: Pas d\'\u00e9cole définie');
      setLoadingServices(false);
      return;
    }

    try {
      console.log('WorkRequestFormPage: Début de la récupération des services techniques pour l\'\u00e9cole:', userSchool.id);
      setLoadingServices(true);
      setError('');
      
      // Utiliser la fonction getServices améliorée pour récupérer les services techniques
      const result = await getServices(userSchool.id);
      console.log('WorkRequestFormPage: Services récupérés:', result);
      
      if (result && result.services && result.services.length > 0) {
        // Filtrer pour ne garder que les services techniques
        const technicalServices = result.services.filter(service => service.role === SERVICE_ROLES.TECHNICAL);
        console.log('WorkRequestFormPage: Services techniques filtrés:', technicalServices);
        
        if (technicalServices.length === 0) {
          console.log('WorkRequestFormPage: Aucun service technique trouvé');
          setError('Aucun service technique n\'est disponible pour votre école.');
        }
        
        setServices(technicalServices);
      } else {
        console.log('WorkRequestFormPage: Aucun service trouvé');
        setError('Aucun service technique n\'est disponible pour votre école.');
        setServices([]);
      }
    } catch (err) {
      console.error('WorkRequestFormPage: Erreur lors de la récupération des services:', err);
      setError('Impossible de charger la liste des services techniques: ' + (err.message || err));
      setServices([]);
    } finally {
      setLoadingServices(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!formData.title || !formData.description || !formData.priority || !formData.assignedServiceId) {
      setError('Veuillez remplir tous les champs obligatoires.');
      return;
    }
    
    // Valider la catégorie personnalisée si "Autre" est sélectionné
    if (formData.category === CATEGORIES.OTHER && !formData.customCategory.trim()) {
      setError('Veuillez préciser la catégorie.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const workRequestData = {
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        category: formData.category === CATEGORIES.OTHER ? formData.customCategory : formData.category,
        location: formData.location,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: currentUser.uid,
        createdByName: userProfile?.displayName || userProfile?.name || currentUser.email || 'Utilisateur',
        schoolId: userSchool?.id,
        requestingServiceId: userService?.id || currentUser.uid,
        requestingServiceName: userService?.name || userProfile?.displayName || userProfile?.name || currentUser.email || null,
        assignedServiceId: formData.assignedServiceId,
        serviceName: services.find(s => s.id === formData.assignedServiceId)?.name || ''
      };

      await addDoc(collection(db, COLLECTIONS.WORK_REQUESTS), workRequestData);
      navigate('/demandes');
    } catch (err) {
      console.error('Erreur lors de la création de la demande:', err);
      setError('Une erreur est survenue lors de la création de la demande.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && services.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Nouvelle demande de travaux
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Titre"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  multiline
                  rows={4}
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="Décrivez en détail le problème ou les travaux nécessaires..."
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Catégorie</InputLabel>
                  <Select
                    name="category"
                    value={formData.category}
                    onChange={(e) => {
                      handleChange(e);
                      // Réinitialiser la catégorie personnalisée si on change de catégorie
                      if (e.target.value !== CATEGORIES.OTHER) {
                        setFormData(prev => ({ ...prev, customCategory: '' }));
                      }
                    }}
                    label="Catégorie"
                    disabled={loading}
                  >
                    {Object.entries(CATEGORIES).map(([key, value]) => (
                      <MenuItem key={key} value={value}>{value}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Champ personnalisé pour "Autre" */}
              {formData.category === CATEGORIES.OTHER && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Précisez la catégorie"
                    name="customCategory"
                    value={formData.customCategory}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Ex: Reprographie, Restauration, etc."
                    helperText="Veuillez préciser le type de service"
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Priorité</InputLabel>
                  <Select
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    label="Priorité"
                    disabled={loading}
                  >
                    {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                      <MenuItem key={value} value={value}>{label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Localisation"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="Ex: Salle 102, Bâtiment A"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={services.length === 0}>
                  <InputLabel>Service technique</InputLabel>
                  <Select
                    name="assignedServiceId"
                    value={formData.assignedServiceId}
                    onChange={handleChange}
                    label="Service technique"
                    disabled={loading || loadingServices || services.length === 0}
                  >
                    {loadingServices ? (
                      <MenuItem disabled>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Chargement des services...
                        </Box>
                      </MenuItem>
                    ) : services.length === 0 ? (
                      <MenuItem disabled value="">
                        Aucun service technique disponible
                      </MenuItem>
                    ) : (
                      services.map((service) => (
                        <MenuItem key={service.id} value={service.id}>
                          <Box>
                            <Typography variant="body1">{service.name}</Typography>
                            {service.email && (
                              <Typography variant="caption" color="text.secondary">
                                {service.email}
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {services.length === 0 && !loadingServices && (
                    <FormHelperText>Aucun service technique disponible</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/demandes')}
                    disabled={loading}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || loadingServices || services.length === 0}
                  >
                    {loading ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Création en cours...
                      </>
                    ) : (
                      'Créer la demande'
                    )}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>
    </Container>
  );
}
